import { Fragment, HTMLAttributes, useState } from 'react';

import { DropdownSection as DropdownSectionType } from '../../config/navDropdowns';
import { inlineBlock } from '../../style/layout';
import * as misc from '../../style/misc';
import { noStyleLink } from '../../style/text';
import { scale } from '../../util/scale';
import HorizontalSeparator from '../UI/HorizontalSeparator';
import NewTag from '../UI/NewTag';
import Fade from '../animation/Fade';
import ArrowDown from '../svgs/ArrowDown';
import DropdownLink from './DropdownLink';

const container = scale({
  position: 'absolute',
  outline: 'none',
  zIndex: 1234,
  paddingTop: '10px',
});

const innerContainer = (isWide?: boolean) =>
  scale({
    backgroundColor: 'white',
    borderRadius: '6px',
    boxShadow: '0px 10px 25px var(--box-shadow-color)',
    cursor: 'auto',
    width: isWide ? '470px' : '200px',
    maxWidth: '90vw',
    height: '100%',
  });

const leftStyle = (isWide?: boolean) =>
  scale({
    top: isWide ? 30 : 25,
    left: isWide ? -40 : 0,
  });

const rightStyle = (isWide?: boolean) =>
  scale({
    top: isWide ? 30 : 25,
    right: isWide ? -40 : 0,
  });

const centerStyle = scale({
  left: '50%',
  transform: 'translate(-50%)',
});

const sectionStyle = scale({
  padding: '6px 0px',
  fontSize: '12px !important',
});

function DropdownSection({
  section,
  onDropdownClose,
}: {
  section: DropdownSectionType;
  onDropdownClose?: () => void;
}) {
  return (
    <div css={sectionStyle}>
      {section.title && (
        <p className="mb-1.5 ml-3.5 mt-2 pt-1 text-[11px] font-medium uppercase tracking-[0.14em] text-gray-500">
          {section.title}
        </p>
      )}
      {section.links.map((link) => {
        if (!link) return;
        const href = link.to + (link.hash ? `#${link.hash}` : '');
        const externalLinkAttributes = link.to.includes('http') // This is a bit hacky
          ? {
              target: '_blank',
              rel: 'noopener noreferrer',
            }
          : {};
        return (
          <DropdownLink
            key={href}
            to={href}
            onClick={() => onDropdownClose?.()}
            {...externalLinkAttributes}
          >
            <span className="flex items-center gap-2.5">
              {link.icon && (
                <span className="flex h-5 w-5 shrink-0 items-center justify-center text-gray-700">
                  {link.icon.component}
                </span>
              )}
              <span className="min-w-0">{link.name}</span>
              {link.isNew && <NewTag />}
            </span>
          </DropdownLink>
        );
      })}
    </div>
  );
}

export function CompactDropdownContent({
  dropdownContent,
  onDropdownClose,
}: {
  dropdownContent: DropdownSectionType[];
  onDropdownClose?: () => void;
}) {
  return (
    <div css={innerContainer()}>
      {dropdownContent
        .filter((section) => section)
        .map((section, index) => (
          <Fragment key={index}>
            {index !== 0 && <HorizontalSeparator />}
            <DropdownSection
              onDropdownClose={onDropdownClose}
              section={section}
            />
          </Fragment>
        ))}
    </div>
  );
}

const Dropdown = (
  props: {
    name?: string;
    customNavItem?: React.ReactNode;
    dropdownContent?: DropdownSectionType[];
    position?: 'left' | 'right' | 'center';
    isWide?: boolean;
    open?: boolean;
    instantExit?: boolean;
    onDropdownClose?: () => void;
    renderDropdownContent?: (
      dropdownContent?: DropdownSectionType[]
    ) => React.ReactNode;
  } & HTMLAttributes<HTMLDivElement>
) => {
  const {
    name,
    customNavItem,
    dropdownContent,
    position,
    isWide,
    open,
    instantExit,
    onDropdownClose,
    renderDropdownContent,
    onMouseEnter,
    onMouseLeave,
    ...restOfProps
  } = props;

  const [showDropdown, setShowDropdown] = useState(false);
  const isDropdownOpen = open ?? showDropdown;

  const positionStyle = position
    ? {
        left: leftStyle(isWide),
        right: rightStyle(isWide),
        center: centerStyle,
      }[position]
    : centerStyle;

  const dropdownPanel = isDropdownOpen ? (
    <div css={innerContainer(isWide)}>
      {renderDropdownContent
        ? renderDropdownContent(dropdownContent)
        : dropdownContent &&
          dropdownContent.length > 0 &&
          dropdownContent
            .filter((section) => section)
            .map((section, index) => (
              <Fragment key={index}>
                {index !== 0 && <HorizontalSeparator />}
                <DropdownSection
                  onDropdownClose={() => {
                    setShowDropdown(false);
                    onDropdownClose?.();
                  }}
                  section={section}
                />
              </Fragment>
            ))}
    </div>
  ) : null;

  return (
    <div
      css={noStyleLink}
      onMouseLeave={(event) => {
        setShowDropdown(false);
        onMouseLeave?.(event);
      }}
      onMouseEnter={(event) => {
        setShowDropdown(true);
        onMouseEnter?.(event);
      }}
      key={name}
      {...restOfProps}
    >
      {customNavItem || (
        <Fragment>
          <span css={inlineBlock}>{name}</span>
          <span css={misc.marginLeft(5)}>
            <ArrowDown color="currentColor" size={10} />
          </span>
        </Fragment>
      )}
      <div css={[container, positionStyle]}>
        {instantExit ? (
          dropdownPanel && <Fade>{dropdownPanel}</Fade>
        ) : (
          <Fade>{dropdownPanel}</Fade>
        )}
      </div>
    </div>
  );
};

export default Dropdown;
